import React, { useState } from 'react';
import './contactUsForm.css';
import CenterStage from '../../Components/CenterStage/CenterStage';
import TwoColumnPoints from '../../Components/TwoColumnPoints/TwoColumnPoints';
import HFTab from '../../Components/HfTabs/HfTabs';
import imgMHSBrazil from '../../assets/BrazilServicos/MHSBrazil.jpeg';
import imgMHS1 from '../../assets/BrazilServicos/MHS1.jpg';
import TextCaption from '../../Components/TextCaption/TextCaption';
import { FaEnvelope, FaExclamationCircle, FaPhone, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import WhatsAppChat from '../../Components/WhatsAppChat/WhatsAppChat';

const OffshoreCampaign = () => {
    const SOURCE = 'offshore-campaign'
    const MAX_MESSAGE_LENGTH = 1000

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        telephone: '',
        cpfCnpj: '',
        message: ''
    })
    const [errors, setErrors] = useState({})
    const [touched, setTouched] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    const formatTelephone = (value) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 10) return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
        return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }

    const formatCpfCnpj = (value) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 11) {
            // CPF format: XXX.XXX.XXX-XX
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        }
        // CNPJ format: XX.XXX.XXX/XXXX-XX
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }

    const validateField = (name, value) => {
        const v = (value ?? '').toString()
        let error = ''

        switch (name) {
            case 'fullName':
                if (!v.trim()) error = 'Full name is required'
                else if (v.trim().length < 2) error = 'Full name must be at least 2 characters'
                break
            case 'email':
                if (!v.trim()) error = 'Email is required'
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) error = 'Please enter a valid email address'
                break
            case 'telephone':
                if (!v.trim()) error = 'Telephone is required'
                else if (v.replace(/\D/g, '').length < 10) error = 'Please enter a valid telephone number'
                break
            case 'cpfCnpj':
                if (!v.trim()) error = 'CPF/CNPJ is required'
                else {
                    const numbers = v.replace(/\D/g, '')
                    if (numbers.length !== 11 && numbers.length !== 14) {
                        error = 'CPF must have 11 digits or CNPJ must have 14 digits'
                    }
                }
                break
            case 'message':
                if (!v.trim()) error = 'Message is required'
                else if (v.trim().length < 10) error = 'Message must be at least 10 characters'
                else if (v.length > MAX_MESSAGE_LENGTH) error = `Message must not exceed ${MAX_MESSAGE_LENGTH} characters`
                break
            default:
                break
        }

        return error
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        let processedValue = value
        if (name === 'telephone') processedValue = formatTelephone(value)
        if (name === 'cpfCnpj') processedValue = formatCpfCnpj(value)

        setFormData(prev => ({ ...prev, [name]: processedValue }))

        if (touched[name]) {
            const error = validateField(name, processedValue)
            setErrors(prev => ({ ...prev, [name]: error }))
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target
        setTouched(prev => ({ ...prev, [name]: true }))
        const error = validateField(name, value)
        setErrors(prev => ({ ...prev, [name]: error }))
    }

    const validateForm = () => {
        const requiredFields = ['fullName', 'email', 'telephone', 'cpfCnpj', 'message']
        const newErrors = {}
        const newTouched = {}

        requiredFields.forEach((key) => {
            newTouched[key] = true
            const err = validateField(key, formData[key])
            if (err) newErrors[key] = err
        })

        setTouched(prev => ({ ...prev, ...newTouched }))
        setErrors(prev => ({ ...prev, ...newErrors }))
        return Object.keys(newErrors).length === 0
    }

    const resetForm = () => {
        setFormData({
            fullName: '',
            email: '',
            telephone: '',
            cpfCnpj: '',
            message: ''
        })
        setTouched({})
        setErrors({})
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        setIsSubmitting(true)
        setSubmitSuccess(false)
        setErrors(prev => ({ ...prev, submit: '' }))

        try {
            const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.hansa-flex.co.in'
            const API_URL = `${API_BASE}/api/hf-brazil/contact/${SOURCE}`

            const composedMessage = `Campaign: Offshore\n\n${formData.message}`

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source: SOURCE,
                    fullName: formData.fullName,
                    email: formData.email,
                    telephone: formData.telephone,
                    cpfCnpj: formData.cpfCnpj,
                    message: composedMessage
                })
            })

            const data = await response.json().catch(() => ({}))
            if (!response.ok) throw new Error(data.error || 'Failed to send')

            setSubmitSuccess(true)
            setTimeout(() => {
                resetForm()
                setSubmitSuccess(false)
                setIsSubmitting(false)
            }, 5000)
        } catch (error) {
            setErrors(prev => ({ ...prev, submit: error.message || 'Failed to send. Please try again later.' }))
            setIsSubmitting(false)
        }
    }

    const { t } = useTranslation();

    const listItems = [
        t("DNV certification and compliance with international standards;"),
        t("IRATA teams, qualified for work at height and in areas that are difficult to access;"),
        t("Testing, inspection, and maintenance processes in line with HANSA-FLEX standards;"),
        t("X-CODE for complete traceability of hoses and connections;"),
    ];
    const listItems2 = [
        "Specialized offshore hydraulic engineering and assembly",
        "Preventive maintenance and detailed inspection of hydraulic hoses and connections",
        "Technical consulting on component selection, hydraulic sizing, and compliance with safety standards",
        "Pressure and hydrostatic safety testing, with regular inspections in accordance with international standards",
        "Commitment to sustainability and responsible environmental management in operations"

    ];

    const imageTextData = [
        {
            title: "THE HANSA-FLEX RANGE OF SERVICES",
            description: "HANSA-FLEX is your reliable partner for everything related to the planning, design, installation, maintenance and optimisation of your hydraulic systems. Our many years of experience mean that we know how to complete complex tasks in a short period of time.",
            buttonName: "LEARN MORE",
            buttonLink: "/MHS",
            image: imgMHS1
        },


    ];

    const tabTitles = [
        { title: "THE HANSA-FLEX RANGE OF SERVICE", eventKey: "Fittings" },

    ];
    return (
        <div>
            <CenterStage imageSrc={imgMHSBrazil} title={"High-Performance Hydraulic Solutions and Components for Oil & Gas"} paragraph={"Trusted partner in hoses, assembly, maintenance, and technical services for offshore and onshore operations. Reduce the risk of hydraulic failure with traceable parts, 24/7 support, and IRATA-certified teams."} />

            <div>
                <TwoColumnPoints points={listItems} title={"Technical Expertise & Integrated Solutions"} />
            </div><br /><br />
            <br /><br />
            <div className="contact-caption ">
                <TextCaption
                    paragraph="In the Oil & Gas sector, conditions are extreme: corrosive environments, pressure, high temperatures, constant vibration, safety requirements, and strict regulations. To operate without interruptions, you need experienced, reliable partners who understand critical hydraulics and custom-made components."
                />
            </div>

            <div className="contact-caption">
                <TextCaption
                    title="At HANSA-FLEX, we offer:"
                    bulletPoints={listItems2}
                />
            </div>

            <div className="contact-caption ">
                <TextCaption
                    paragraph="Fill out the form or click on the WhatsApp button to contact us."
                />
            </div>

            <div className='contact-form-container'>
                <h1 className="contact-form-title">{t('Offshore / Oil & Gas Contact Form')}</h1>
                <p className="contact-form-instruction">{t('Please fill out the form below')}</p>

                <form className="contact-form" onSubmit={handleSubmit} style={{ pointerEvents: isSubmitting ? 'none' : 'auto', opacity: isSubmitting ? 0.7 : 1 }}>
                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="text"
                                name="fullName"
                                placeholder={t('Full Name*')}
                                value={formData.fullName}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.fullName ? 'input-error' : ''} ${touched.fullName && !errors.fullName && formData.fullName ? 'input-valid' : ''}`}
                                aria-label="Name"
                                aria-required="true"
                                aria-invalid={!!errors.fullName}
                                disabled={isSubmitting}
                                autoFocus
                            />
                            <FaUser className="input-icon" />
                        </div>
                        {errors.fullName && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.fullName)}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="email"
                                name="email"
                                placeholder={t('Email*')}
                                value={formData.email}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.email ? 'input-error' : ''} ${touched.email && !errors.email && formData.email ? 'input-valid' : ''}`}
                                aria-label="E-mail"
                                aria-required="true"
                                aria-invalid={!!errors.email}
                                disabled={isSubmitting}
                            />
                            <FaEnvelope className="input-icon" />
                        </div>
                        {errors.email && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.email)}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="tel"
                                name="telephone"
                                placeholder={t('Telephone*')}
                                value={formData.telephone}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.telephone ? 'input-error' : ''} ${touched.telephone && !errors.telephone && formData.telephone ? 'input-valid' : ''}`}
                                aria-label="Telephone"
                                aria-required="true"
                                aria-invalid={!!errors.telephone}
                                disabled={isSubmitting}
                            />
                            <FaPhone className="input-icon" />
                        </div>
                        {errors.telephone && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.telephone)}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="text"
                                name="cpfCnpj"
                                placeholder={t('CPF/CNPJ*')}
                                value={formData.cpfCnpj}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.cpfCnpj ? 'input-error' : ''} ${touched.cpfCnpj && !errors.cpfCnpj && formData.cpfCnpj ? 'input-valid' : ''}`}
                                aria-label="CPF/CNPJ"
                                aria-required="true"
                                aria-invalid={!!errors.cpfCnpj}
                                disabled={isSubmitting}
                            />
                        </div>
                        {errors.cpfCnpj && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.cpfCnpj)}
                            </span>
                        )}
                    </div>

                    <div className="form-group message-group">
                        <textarea
                            name="message"
                            placeholder={t('Message*')}
                            value={formData.message}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            maxLength={MAX_MESSAGE_LENGTH}
                            className={`form-textarea ${errors.message ? 'input-error' : ''} ${touched.message && !errors.message && formData.message ? 'input-valid' : ''} ${formData.message.length >= MAX_MESSAGE_LENGTH ? 'char-limit-reached' : ''}`}
                            rows="6"
                            aria-label="Message"
                            aria-required="true"
                            aria-invalid={!!errors.message}
                            disabled={isSubmitting}
                        />
                        <div className="textarea-footer">
                            {errors.message && (
                                <span className="error-message" role="alert">
                                    <FaExclamationCircle className="error-icon" />
                                    {t(errors.message)}
                                </span>
                            )}
                        </div>
                    </div>

                    {errors.submit && (
                        <div className="error-message" style={{ marginTop: '10px', marginBottom: '10px' }} role="alert">
                            <FaExclamationCircle className="error-icon" />
                            {t(errors.submit)}
                        </div>
                    )}

                    {submitSuccess && (
                        <div style={{ marginTop: '10px', marginBottom: '10px', color: 'green' }}>
                            {t('Submitted successfully. Please check your email.')}
                        </div>
                    )}

                    <button
                        type="submit"
                        className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                        disabled={isSubmitting}
                        aria-label="Submit Offshore form"
                    >
                        {isSubmitting ? t('Sending...') : t('Send')}
                    </button>
                </form>
            </div>
            <div>
                {/* SectionHeader removed per request */}
                <HFTab imageTextData={imageTextData} tabTitles={tabTitles} />
            </div>
            <br /><br />
            <WhatsAppChat phoneNumber="+55 (47) 99726-1083" />
        </div>
    );
};

export default OffshoreCampaign;